import { trpc } from './trpc'
import { inferRouterOutputs, inferRouterInputs } from '@trpc/server'
import { AppRouter } from '@tagadapay/trpc-types'
import { useToast } from '@/components/ui/use-toast'

type OutputRouter = inferRouterOutputs<AppRouter>
type InputRouter = inferRouterInputs<AppRouter>

// Output types
export type EmailTemplatesListOutput = OutputRouter['emailTemplates']['list']
export type EmailTemplateItem = EmailTemplatesListOutput['templates'][number]
export type CreateEmailTemplateOutput = OutputRouter['emailTemplates']['create']
export type UpdateEmailTemplateOutput = OutputRouter['emailTemplates']['update']
export type DeleteEmailTemplateOutput = OutputRouter['emailTemplates']['delete']
export type TestEmailTemplateOutput = OutputRouter['emailTemplates']['test']
export type PreviewEmailTemplateOutput = OutputRouter['emailTemplates']['preview']

// Input types
export type ListEmailTemplatesInput = InputRouter['emailTemplates']['list']
export type CreateEmailTemplateInput = InputRouter['emailTemplates']['create']
export type UpdateEmailTemplateInput = InputRouter['emailTemplates']['update']
export type DeleteEmailTemplateInput = InputRouter['emailTemplates']['delete']
export type TestEmailTemplateInput = InputRouter['emailTemplates']['test']
export type PreviewEmailTemplateInput = InputRouter['emailTemplates']['preview']

// Query hooks
export const useEmailTemplatesList = (params: ListEmailTemplatesInput, options?: {
	enabled?: boolean;
	refetchOnMount?: boolean | 'always';
	refetchOnWindowFocus?: boolean;
}) => {
	const { enabled = true, ...otherOptions } = options || {};
	return trpc.emailTemplates.list.useQuery(params, {
		enabled: enabled && !!params.storeId,
		...otherOptions,
	})
}

export const usePreviewEmailTemplate = (params: PreviewEmailTemplateInput, enabled = true) => {
	return trpc.emailTemplates.preview.useQuery(params, {
		enabled: enabled && !!params.templateId,
	})
}

// Mutation hooks
export const useCreateEmailTemplate = () => {
	const { toast } = useToast()
	const utils = trpc.useUtils()

	return trpc.emailTemplates.create.useMutation({
		onSuccess: () => {
			utils.emailTemplates.list.invalidate()
			toast({
				title: 'Success',
				description: 'Email template created successfully',
			})
		},
		onError: (error) => {
			toast({
				title: 'Error',
				description: error.message || 'Failed to create email template',
				variant: 'destructive',
			})
		},
	})
}

export const useUpdateEmailTemplate = () => {
	const { toast } = useToast()
	const utils = trpc.useUtils()

	return trpc.emailTemplates.update.useMutation({
		onSuccess: () => {
			utils.emailTemplates.list.invalidate()
			toast({
				title: 'Success',
				description: 'Email template updated successfully',
			})
		},
		onError: (error) => {
			toast({
				title: 'Error',
				description: error.message || 'Failed to update email template',
				variant: 'destructive',
			})
		},
	})
}

export const useDeleteEmailTemplate = () => {
	const { toast } = useToast()
	const utils = trpc.useUtils()

	return trpc.emailTemplates.delete.useMutation({
		onSuccess: () => {
			utils.emailTemplates.list.invalidate()
			toast({
				title: 'Success',
				description: 'Email template deleted successfully',
			})
		},
		onError: (error) => {
			toast({
				title: 'Error',
				description: error.message || 'Failed to delete email template',
				variant: 'destructive',
			})
		},
	})
}

export const useTestEmailTemplate = () => {
	const { toast } = useToast()

	return trpc.emailTemplates.test.useMutation({
		onSuccess: () => {
			toast({
				title: 'Success',
				description: 'Test email sent successfully',
			})
		},
		onError: (error) => {
			toast({
				title: 'Error',
				description: error.message || 'Failed to send test email',
				variant: 'destructive',
			})
		},
	})
}

